import { generateQueryParamsString, GET_METHOD, Request } from '@/lib/http';
import {
  buildAnalysisFormulations,
  buildAnalysisSummary,
  buildFormularyAnalysis,
} from './__fixtures__/analysisFixtures';

// The scenario body carries the scope split across `dateRange` and `filters`;
// the fixtures take it in the flat shape the rest of the module uses.
const scopeOf = (scenario = {}) => ({
  medicationName: scenario.medicationName,
  startDate: scenario.dateRange?.start || '',
  endDate: scenario.dateRange?.end || '',
  facilities: scenario.filters?.facilities || [],
  patientPopulations: scenario.filters?.patientPopulations || [],
});

// AnalysisService
//
// Every server-sourced dataset the Scenario Analysis (Analyze) page needs:
// the medication's current formulations, and the two counterfactual impact
// calculations ("what happens if we switch these formulations to that one").
//
// THIS FILE IS THE LOOKER SEAM. It contains no SQL and no query construction —
// only endpoint names and request bodies. In the demo, `lib/demoBackend.js`
// rewrites these into the backend's `/query?sql=` passthrough; against the real
// backend these hit Go handlers that call Looker. The service code itself is
// exactly what ships to the target project.
//
// Follows the target application's service convention: build query params with
// `generateQueryParamsString`, return `new Request(url).send()`. Response
// shaping happens in the caller (the `analysis` store module's actions).
//
// ---------------------------------------------------------------------------
// Relationship to Looker in the target project
// ---------------------------------------------------------------------------
// In the target project the frontend NEVER talks to Looker. Dashboards are
// signed SSO embed URLs dropped into an <iframe> and driven by postMessage; the
// browser has no Looker credentials and no query path. Nothing in this service
// contradicts that: these are ordinary backend endpoints, and the backend
// decides how to satisfy them.
//
// Analyze is not a dashboard. It's an interactive what-if tool whose numbers
// depend on user input (a hypothetical concentration and volume per formulation),
// so an embedded dashboard can't express it. The closest existing precedent is
// the autocomplete endpoint, which uses Looker as a SQL-GENERATION service —
// asking Looker for a tile's generated SQL and running that against the warehouse
// directly. The same shape works here: model the counterfactual in LookML, have
// the backend obtain/execute it, and return the shapes below.
//
// What matters for the port is that this file names endpoints and shapes only.
// Whether the backend answers them from LookML-generated SQL, a parameterized
// derived table, or direct warehouse queries is invisible to every consumer.
//
// ---------------------------------------------------------------------------
// Why the impact endpoints are POSTs that take a "scenario"
// ---------------------------------------------------------------------------
// The impact analysis is NOT an aggregation of existing rows — it substitutes a
// hypothetical concentration/volume into historical infusion data and recomputes
// container counts and flow rates from it:
//
//   new_containers = GREATEST(CEIL(units_infused / (new_conc * new_vol)), expirations)
//   new_min_rate   = concentration * min_rate / new_concentration
//
// Those hypothetical values are user input, one pair per current formulation, so
// the request body is a *scenario description*, not a filter set. How that
// scenario is satisfied (a parameterized LookML derived table, a base-grain
// Explore aggregated in Go, or a per-pair fan-out) is a backend decision the
// frontend must not encode.

const baseURL = '/api/v1';

// Both impact endpoints take the same body — a *scenario description* built by
// the `analysis` store module (see `toScenarioBody` there), not a filter set.
// Shape:
//
//   { medicationName,
//     dateRange: { start, end },                       // YYYY-MM-DD
//     filters:   { facilities: [], patientPopulations: [] },
//     changes:   [{ currentFormulation, updatedFormulation,
//                   current, updated }] }               // structured, or null
//
// Formulations are sent BOTH as the canonical string (the frontend's join key
// and display value) and in structured form, so the server never has to
// re-implement the parsing regex; `null` signals an unparseable formulation,
// which the server should skip rather than guess at.

export default {
  // Current formulations + utilization/waste for one medication.
  // Target: GET /formulations?medication=&start=&end=&facilities=&patientPopulations=
  //              [&minInfusions=&includeFlowRates=]
  //      -> { formulations: [{ formulation, infusions, wasteWeightedPct,
  //                            concentrationValue, concentrationUnit,
  //                            volumeValue, volumeUnit,
  //                            adultInfusions, pediatricInfusions,
  //                            minRate, maxRate }] }
  //
  // Shared by Analyze and Investigate. Two optional params separate their needs:
  //
  //   minInfusions      applies a server-side threshold. Investigate passes it;
  //                     Analyze omits it because it filters client-side (its
  //                     min-infusions control re-buckets without refetching).
  //   includeFlowRates  adds minRate/maxRate, which come from segment-group grain
  //                     and cost an extra join. Only Investigate's table shows them.
  //
  // ONE WASTE FIELD. `wasteWeightedPct` is the infusion-weighted ratio
  // SUM(units_wasted)/SUM(units_dispensed), read by Analyze and Investigate
  // alike and equal to the counterfactual's `current_waste`.
  //
  // A second field, `wastePercentage`, was returned until 2026-08-10 for
  // Analyze. It divided drug mass by container volume — a dimensional error, not
  // an alternative weighting — and has been removed rather than reimplemented.
  // A Looker-backed endpoint must model ONE waste measure; see the note on
  // `buildFormulationsSQL` in lib/demoBackend.js for the full derivation.
  getFormulations({
    medicationName,
    startDate,
    endDate,
    facilities,
    patientPopulations,
    minInfusions,
    includeFlowRates,
  }) {
    // const queryString = generateQueryParamsString({
    //   medication: medicationName,
    //   start: startDate || '',
    //   end: endDate || '',
    //   facilities: facilities || [],
    //   patientPopulations: patientPopulations || [],
    //   // Omitted entirely when absent, so the server can distinguish
    //   // "no threshold" from "a threshold of 0".
    //   ...(minInfusions != null ? { minInfusions } : {}),
    //   ...(includeFlowRates ? { includeFlowRates: true } : {}),
    // });
    // const request = new Request(`${baseURL}/formulations${queryString}`, { method: GET_METHOD });
    // return request.send();
    return Promise.resolve({
      formulations: buildAnalysisFormulations({
        medicationName,
        startDate,
        endDate,
        facilities,
        patientPopulations,
        minInfusions,
      }),
    });
  },

  // Monthly infusion counts per formulation — the Investigate usage-trends chart.
  // Target: GET /investigation/usage-trends?medication=&start=&end=&facilities=
  //              &patientPopulations=&minInfusions=
  //      -> { trends: [{ year, month, formulation, infusions }] }
  //
  // The 6-full-month window is computed by the caller
  // (`modules/formulary/utils/trendWindow.js`) and passed as ordinary start/end dates —
  // the server needs no notion of "full months". Rows are one per
  // (month, formulation); months with no data for a formulation are simply
  // absent rather than zero-filled, and the chart treats missing as zero.
  getUsageTrends({ medicationName, startDate, endDate, facilities, patientPopulations, minInfusions }) {
    const queryString = generateQueryParamsString({
      medication: medicationName,
      start: startDate || '',
      end: endDate || '',
      facilities: facilities || [],
      patientPopulations: patientPopulations || [],
      minInfusions: minInfusions ?? 5,
    });
    const request = new Request(`${baseURL}/investigation/usage-trends${queryString}`, {
      method: GET_METHOD,
    });
    return request.send();
  },

  // Per-row counterfactual metrics: one result per requested change, in the SAME
  // ORDER as `changes` (the caller zips positionally).
  // Target: POST /analysis/formulary
  //      -> { results: [{ currentWaste, updatedWaste, currentBagsPerDay,
  //                       updatedBagsPerDay, currentMinRate, updatedMinRate,
  //                       currentMaxRate, updatedMaxRate,
  //                       currentInfusions, updatedInfusions }] }
  //
  // Takes ALL pairs in one request. The legacy implementation issued one query
  // per formulation from the browser; batching lets the backend decide whether to
  // fan out, batch, or cache — which matters against Looker's latency.
  getFormularyAnalysis(scenario) {
    // const request = new Request(`${baseURL}/analysis/formulary`, {
    //   method: POST_METHOD,
    //   body: scenario,
    // });
    // return request.send();
    return Promise.resolve({ results: buildFormularyAnalysis(scopeOf(scenario), scenario.changes) });
  },

  // Cumulative impact summary across every proposed change at once.
  // Target: POST /analysis/summary
  //      -> { waste, unitsDispensed, infusionsDispensed,
  //           pctPatientsWithMoreInfusions, cost }
  //         each: { intValue|stringValue, isIncrease, text }
  //
  // NOTE: `cost` is computed client-side from user-entered $/infusion and always
  // overrides whatever the server returns, so the backend may return $0.
  getAnalysisSummary(scenario) {
    // const request = new Request(`${baseURL}/analysis/summary`, {
    //   method: POST_METHOD,
    //   body: scenario,
    // });
    // return request.send();
    return Promise.resolve(buildAnalysisSummary(scopeOf(scenario), scenario.changes));
  },
};
